import { Injectable } from '@angular/core';
import { Auth, idToken } from '@angular/fire/auth';
import { where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  CompetitionType,
  Id,
  LicenseUserRate,
  Optional,
  PublicInfoUser,
  Team,
  User,
  UserRole,
} from '@models';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs';
import { AuthService } from './auth.service';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TeamsService } from './team.service';
import { TenantContextService } from './tenant-context.service';
import { PlayerInscriptionRequest, PlayerInscriptionResponse } from '@models';
import { CheckIfUserIsRegisteredResponse } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public uid$: Observable<Id>;
  public user$: Observable<User>;
  public myProfileWithLicenses$: Observable<{
    user: User;
    licenses: LicenseUserRate[];
  }>;
  public roles$: Observable<UserRole[]>;
  public isAdmin$: Observable<boolean>;
  public isRefereeCoordinator$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public isSchoolManager$: Observable<boolean>;
  public isDelegateOfHighLeague$: Observable<boolean>;

  constructor(
    private firestore: FirestoreService,
    private auth: AuthService,
    private seasonManager: SeasonManagerService,
    private functions: Functions,
    private clubService: ClubsService,
    private teamsService: TeamsService,
    private tenantContext: TenantContextService,
  ) {
    this.uid$ = this.auth.uid$;

    // user$ reacts to every claims change (login, impersonation start/stop)
    // AND to explicit tenant switches (switchTenant).
    // combineLatest ensures the user document is always loaded from the active tenant.
    this.user$ = combineLatest([
      this.tenantContext.claimsOrNull$.pipe(
        filter((c): c is NonNullable<typeof c> | null => c !== undefined),
      ),
      this.tenantContext.tenantId$,
    ]).pipe(
      switchMap(([claims, tenantId]) => {
        if (!claims) return EMPTY;

        if (claims.superAdmin === true) {
          return this.auth.uid$.pipe(
            first(),
            map(
              (uid): User => ({
                id: uid,
                name: 'Super',
                firstLastName: 'Admin',
                secondLastName: '',
                email: '',
                documentType: 'dni',
                document: '',
                phone: '',
                birthDate: new Date(0),
                address: {
                  street: '',
                  city: '',
                  province: '',
                  zip: '',
                  country: '',
                } as any,
                gender: 'MALE' as any,
                roles: [UserRole.SUPER_ADMIN],
              }),
            ),
          );
        }

        if (!tenantId) return EMPTY;

        return this.auth.uid$.pipe(
          switchMap((uid) =>
            this.firestore.getDocument<User>(
              this.tenantContext.collections.USERS,
              uid,
            ),
          ),
        );
      }),
      catchError(() => EMPTY),
      shareReplay({ refCount: true }),
      takeUntil(this.auth.onLogout),
    );

    this.myProfileWithLicenses$ = this.user$.pipe(
      switchMap((user) =>
        this.getUserLicenses(user.id).pipe(
          map((licenses) => ({ user, licenses })),
        ),
      ),
    );

    this.roles$ = this.user$.pipe(map((u) => u.roles ?? []));

    // Derive directly from JWT claims — no Firestore doc required.
    // This ensures the menu appears even when the user document doesn't exist yet.
    this.isAdmin$ = this.tenantContext.activeRole$.pipe(
      map((role) => role === 'ADMIN'),
    );
    this.isRefereeCoordinator$ = this.tenantContext.activeRole$.pipe(
      map((role) => role === 'REFEREE_COORDINATOR'),
    );

    // Single source of truth: the club document knows who its manager is.
    // No need to duplicate role info on the user — just check club.managerId.
    this.isManager$ = this.user$.pipe(
      switchMap((user) => {
        if (!user?.clubId) return of(false);
        return this.clubService.get(user.clubId).pipe(
          map((club) => club.managerId === user.id),
          catchError(() => of(false)),
        );
      }),
      shareReplay({ refCount: true }),
      takeUntil(this.auth.onLogout),
    );

    this.isSchoolManager$ = this.user$.pipe(
      switchMap((user) => {
        if (!user?.clubId) return of(false);
        return this.clubService.get(user.clubId).pipe(
          map((club) => club.schoolManagerId === user.id),
          catchError(() => of(false)),
        );
      }),
      shareReplay({ refCount: true }),
      takeUntil(this.auth.onLogout),
    );

    this.isDelegateOfHighLeague$ = this.user$.pipe(
      switchMap((user) => {
        if (!user?.clubId) return of(false);
        return this.teamsService.getByClub(user.clubId).pipe(
          map((teamsByClub) =>
            teamsByClub.some(
              (team) =>
                team.season?.type === CompetitionType.LEAGUE &&
                (team.season as any)?.level >= 4 &&
                team.delegatesIds?.includes(user.id),
            ),
          ),
          catchError(() => of(false)),
        );
      }),
      shareReplay({ refCount: true }),
      takeUntil(this.auth.onLogout),
    );
  }

  get(id: string) {
    return this.firestore.getDocument<User>(
      this.tenantContext.collections.USERS,
      id,
    );
  }

  createOrUpdate(user: Optional<User, 'id'>) {
    user.document = user.document.toUpperCase();
    return user.id ? this.update(user.id, user) : this.createUser(user);
  }

  private createUser(user: Omit<User, 'id'>) {
    return this.firestore.create<User>(
      this.tenantContext.collections.USERS,
      user,
    );
  }

  update(id: string, user: Partial<User>) {
    return this.firestore.update<User>(this.tenantContext.collections.USERS, {
      id,
      ...user,
    });
  }

  updateUserToken(fcmToken: string): Promise<void> {
    return firstValueFrom(
      this.uid$.pipe(
        first(),
        switchMap((uid) => this.update(uid, { fcmToken })),
      ),
    );
  }

  getUserLicenses(userId: string) {
    return this.seasonManager.season$.pipe(
      switchMap((seasonYear) =>
        this.firestore.listDocuments<LicenseUserRate>(
          [this.tenantContext.collections.USERS, userId, 'user_licenses'].join(
            '/',
          ),
          where('seasonYear', '==', seasonYear),
        ),
      ),
      shareReplay(1),
    );
  }

  getPlayerPublicInfo(userId: string) {
    // Endpoint público ("seguir el deporte" sin estar federado): pasamos el
    // tenantId explícito porque sin sesión no hay token del que derivarlo.
    const callable = httpsCallable<
      { userId: string; tenantId: string | null },
      PublicInfoUser
    >(this.functions, 'getPlayerPublicInfo');
    return callable({ userId, tenantId: this.tenantContext.tenantIdOrNull });
  }

  findPlayer(data: PlayerInscriptionRequest) {
    const callable = httpsCallable<
      PlayerInscriptionRequest,
      PlayerInscriptionResponse
    >(this.functions, 'findPlayer');
    return callable(data);
  }

  getPlayers(clubId: string): Observable<User[]> {
    return this.firestore
      .listDocuments<Team>(
        this.tenantContext.collections.TEAMS,
        where('clubId', '==', clubId),
      )
      .pipe(
        map((teams) => {
          const playerIds = [
            ...new Set(teams.flatMap((team) => (team as any).playersIds || [])),
          ];
          return playerIds;
        }),
        switchMap((playerIds: string[]) => {
          if (playerIds.length === 0) {
            return of([]);
          }
          return combineLatest(
            playerIds.map((playerId) =>
              this.firestore.getDocument<User>(
                this.tenantContext.collections.USERS,
                playerId,
              ),
            ),
          );
        }),
      );
  }

  async checkIfDuplicatedDocument(document: string): Promise<User | undefined> {
    return firstValueFrom(
      this.firestore.findDocument<User>(
        this.tenantContext.collections.USERS,
        where('document', '==', document.toUpperCase()),
      ),
    ).then((u) => u ?? undefined);
  }

  async checkIfDuplicatedPhone(phone: string): Promise<User | undefined> {
    return firstValueFrom(
      this.firestore.findDocument<User>(
        this.tenantContext.collections.USERS,
        where('phone', '==', phone),
      ),
    ).then((u) => u ?? undefined);
  }

  async checkIfDuplicatedEmail(email: string): Promise<User | undefined> {
    return firstValueFrom(
      this.firestore.findDocument<User>(
        this.tenantContext.collections.USERS,
        where('email', '==', email),
      ),
    ).then((u) => u ?? undefined);
  }

  async checkIfUserIsRegistered(
    request:
      | string
      | Omit<import('@models').UserRegistrationFindRequest, 'tenantId'>,
  ) {
    if (typeof request === 'string') {
      const user = await this.checkIfDuplicatedEmail(request);
      return { data: user ?? false };
    }
    const callable = httpsCallable<
      import('@models').UserRegistrationFindRequest,
      CheckIfUserIsRegisteredResponse
    >(this.functions, 'checkIfUserIsRegistered');
    return callable({ ...request, tenantId: this.tenantContext.tenantId });
  }

  async createUserAndAuth(user: Omit<User, 'roles'> | Partial<User>) {
    const callable = httpsCallable<any, { uid: string }>(
      this.functions,
      'createUserAndAuth',
    );
    // El callable es público/pre-login y exige `tenantId` en el payload (no hay
    // auth todavía), igual que `checkIfUserIsRegistered`. En build móvil
    // `tenantContext.tenantId` resuelve al tenant de la app.
    return callable({ ...user, tenantId: this.tenantContext.tenantId });
  }

  /**
   * Alta de un jugador/delegado durante la inscripción (gestor de club). La
   * creación y los duplicados se resuelven en la Cloud Function con Admin SDK,
   * porque el gestor no puede escribir/consultar `users` bajo las security rules.
   */
  createInscriptionPlayer(user: Partial<User> & { clubId: string }) {
    const callable = httpsCallable<
      Partial<User> & { clubId: string; tenantId: string },
      | { success: true; userId: string }
      | { success: false; duplicated: 'document' | 'phone' | 'email' }
    >(this.functions, 'createInscriptionPlayer');
    return callable({ ...user, tenantId: this.tenantContext.tenantId });
  }
}
